import Cookie from 'universal-cookie';
import { baseUrl } from '../../utils/auth';
import {
  DEMO_SHARE_URL,
  DEMO_SHARE_USAGE_ACTION,
  recordDemoShareUsage,
} from './demoShareApi';

const OPEN_ENGAGEMENT_KEY = 'demoShareOpenEngagement';
const EXTERNAL_POLL_MS = 1000;

let externalWatchTimer = null;
let unloadListenersInstalled = false;

const readAuthHeaders = () => {
  const cookie = new Cookie();
  const tenantId = cookie.get('entityId');
  const accessToken = cookie.get('user');
  const authorization = cookie.get('authorization');
  return {
    'Content-Type': 'application/json',
    'X-tenantID': tenantId,
    'X-Authorization': `Bearer ${accessToken}`,
    Authorization: `Bearer ${authorization}`,
  };
};

const isSharedDemoPath = () =>
  typeof window !== 'undefined' &&
  String(window.location.pathname || '').includes('/shared-demo');

export const readOpenDemoShareEngagement = () => {
  try {
    const raw = sessionStorage.getItem(OPEN_ENGAGEMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.engagementId || !parsed?.shareId || !parsed?.tileKey) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearOpenDemoShareEngagement = () => {
  sessionStorage.removeItem(OPEN_ENGAGEMENT_KEY);
};

export const persistOpenDemoShareEngagement = (engagement) => {
  if (!engagement?.engagementId || !engagement?.shareId || !engagement?.tileKey) {
    return;
  }
  sessionStorage.setItem(
    OPEN_ENGAGEMENT_KEY,
    JSON.stringify({
      ...engagement,
      trackMode: engagement.trackMode || 'external',
      openedAt: engagement.openedAt || Date.now(),
      accumulatedActiveMs: Number(engagement.accumulatedActiveMs) || 0,
      activeSegmentStartedAt: engagement.activeSegmentStartedAt || null,
    })
  );
};

/**
 * Whether the current engagement should be accruing time right now.
 * - in-app: only while this tab is visible, and not on /shared-demo (skip shell time)
 * - external: while opener is not focused/visible (user is likely on the child link tab)
 */
const shouldAccrueActiveTime = (engagement) => {
  if (typeof document === 'undefined') return false;
  const trackMode = engagement?.trackMode || 'external';

  if (trackMode === 'in-app') {
    if (isSharedDemoPath()) return false;
    return document.visibilityState === 'visible';
  }

  // External child tabs are cross-origin; best-effort: count while Shared Demo
  // is backgrounded, pause when the user returns focus to Shared Demo.
  return document.visibilityState === 'hidden' || !document.hasFocus();
};

const flushActiveSegmentMs = (engagement) => {
  let accumulated = Number(engagement?.accumulatedActiveMs) || 0;
  const segmentStart = Number(engagement?.activeSegmentStartedAt) || 0;
  if (segmentStart) {
    accumulated += Math.max(0, Date.now() - segmentStart);
  }
  return accumulated;
};

/** Pause/resume the active timer based on focus/visibility and persist. */
export const syncDemoShareEngagementActiveTime = () => {
  const engagement = readOpenDemoShareEngagement();
  if (!engagement) return null;

  const accumulatedActiveMs = flushActiveSegmentMs(engagement);
  const shouldRun = shouldAccrueActiveTime(engagement);
  const next = {
    ...engagement,
    accumulatedActiveMs,
    activeSegmentStartedAt: shouldRun ? Date.now() : null,
  };
  persistOpenDemoShareEngagement(next);
  return next;
};

const getActiveDurationSeconds = (engagement) => {
  if (!engagement) return 0;
  return Math.max(0, Math.round(flushActiveSegmentMs(engagement) / 1000));
};

const postLinkCloseKeepalive = (engagement, durationSeconds) => {
  if (!engagement?.shareId || !engagement?.tileKey || !engagement?.engagementId) {
    return false;
  }
  const url = `${baseUrl()}/${DEMO_SHARE_URL}/shared/${encodeURIComponent(
    engagement.shareId
  )}/usage`;
  const body = JSON.stringify({
    action: DEMO_SHARE_USAGE_ACTION.LINK_CLOSE,
    tileKey: engagement.tileKey,
    engagementId: engagement.engagementId,
    durationSeconds,
  });

  try {
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        headers: readAuthHeaders(),
        body,
        keepalive: true,
      }).catch(() => {});
      return true;
    }
  } catch {
    // fall through
  }

  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      return navigator.sendBeacon(url, blob);
    }
  } catch {
    return false;
  }
  return false;
};

/**
 * Close the current open link engagement (if any).
 * Uses keepalive on unload paths so the request can finish during navigation.
 */
export const closeOpenDemoShareEngagement = async ({
  useKeepalive = false,
} = {}) => {
  const engagement = readOpenDemoShareEngagement();
  if (!engagement) return null;

  const durationSeconds = getActiveDurationSeconds(engagement);

  clearOpenDemoShareEngagement();
  stopExternalTabWatch();

  if (useKeepalive) {
    postLinkCloseKeepalive(engagement, durationSeconds);
    return { ...engagement, durationSeconds };
  }

  try {
    await recordDemoShareUsage(engagement.shareId, {
      action: DEMO_SHARE_USAGE_ACTION.LINK_CLOSE,
      tileKey: engagement.tileKey,
      engagementId: engagement.engagementId,
      durationSeconds,
    });
  } catch (error) {
    console.warn('Failed to record demo share link close', error);
  }

  return { ...engagement, durationSeconds };
};

export const stopExternalTabWatch = () => {
  if (externalWatchTimer) {
    clearInterval(externalWatchTimer);
    externalWatchTimer = null;
  }
};

/**
 * Poll an opened child tab and close engagement when it disappears.
 */
export const watchExternalDemoShareTab = (childWindow) => {
  stopExternalTabWatch();
  if (!childWindow || !readOpenDemoShareEngagement()) return;

  // Child open typically backgrounds Shared Demo — start accruing if appropriate.
  syncDemoShareEngagementActiveTime();

  externalWatchTimer = setInterval(() => {
    const engagement = readOpenDemoShareEngagement();
    if (!engagement) {
      stopExternalTabWatch();
      return;
    }

    // Keep active-time state in sync even if focus events were missed.
    syncDemoShareEngagementActiveTime();

    let closed = false;
    try {
      closed = Boolean(childWindow.closed);
    } catch {
      closed = true;
    }
    if (closed) {
      stopExternalTabWatch();
      closeOpenDemoShareEngagement();
    }
  }, EXTERNAL_POLL_MS);
};

/**
 * Open link engagement, persist it, and return the API response.
 * trackMode:
 * - external: close when child tab closes or Shared Demo unloads
 * - in-app: keep open across Shared Demo → live app redirect; close on later unload/logout
 *
 * Duration is active/focus time only (not wall-clock while backgrounded).
 */
export const openDemoShareLinkEngagement = async ({
  shareId,
  tileKey,
  workspaceKey,
  storyId,
  linkId,
  trackMode = 'external',
}) => {
  if (!shareId || !tileKey || !linkId) return null;

  // One open engagement at a time.
  await closeOpenDemoShareEngagement();

  const data = await recordDemoShareUsage(shareId, {
    action: DEMO_SHARE_USAGE_ACTION.LINK_OPEN,
    tileKey,
    workspaceKey,
    storyId,
    linkId,
  });

  const engagementId = data?.engagementId;
  if (!engagementId) return data;

  persistOpenDemoShareEngagement({
    shareId,
    tileKey,
    workspaceKey: workspaceKey || '',
    storyId: storyId || null,
    linkId,
    engagementId,
    trackMode,
    openedAt: Date.now(),
    accumulatedActiveMs: 0,
    // Start paused; sync below decides whether to accrue immediately.
    activeSegmentStartedAt: null,
  });

  syncDemoShareEngagementActiveTime();

  return data;
};

/**
 * Install once: active-time sync + close on unload.
 * In-app engagements survive the Shared Demo → live app redirect.
 */
export const installDemoShareEngagementUnloadHandlers = () => {
  if (typeof window === 'undefined' || unloadListenersInstalled) return;
  unloadListenersInstalled = true;

  const onActiveStateChange = () => {
    if (!readOpenDemoShareEngagement()) return;
    syncDemoShareEngagementActiveTime();
  };

  document.addEventListener('visibilitychange', onActiveStateChange);
  window.addEventListener('focus', onActiveStateChange);
  window.addEventListener('blur', onActiveStateChange);
  window.addEventListener('pageshow', onActiveStateChange);

  const onPageHide = () => {
    const engagement = readOpenDemoShareEngagement();
    if (!engagement) return;
    if (engagement.trackMode === 'in-app' && isSharedDemoPath()) {
      // Navigating into the live app — pause Shared Demo shell time, keep engagement.
      persistOpenDemoShareEngagement({
        ...engagement,
        accumulatedActiveMs: flushActiveSegmentMs(engagement),
        activeSegmentStartedAt: null,
      });
      return;
    }
    closeOpenDemoShareEngagement({ useKeepalive: true });
  };

  window.addEventListener('pagehide', onPageHide);

  // Resume correctly after a live-app landing / refresh with an open engagement.
  syncDemoShareEngagementActiveTime();
};
